import BaseModel from './base-modeles/base-model';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    IClrScheme,
    IThemeItem,
    IThemeModel,
    IThemeSelectItem,
} from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';
import {
    ICTColorScheme,
    STSchemeColorVal,
    ICTBaseStyles,
} from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import { ThemePropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/page/theme-property-converter';
import { observable } from 'mobx';
import { IThemeDataModel, DeltaOf } from '@tencent/slide-sdk-model';
import { rgba2hex } from '@src/slide/infrastructure/module/independent/color/transform';
import i18n from '@i18next/i18n';

const DEFAULT_THEME_COLOR = 'FFFFFF';

export const STSchemeColorValList = [
    'PBDEFAULT',
    'bg1',
    'tx1',
    'bg2',
    'tx2',
    'accent1',
    'accent2',
    'accent3',
    'accent4',
    'accent5',
    'accent6',
    'hlink',
    'folHlink',
    'phClr',
    'dk1',
    'lt1',
    'dk2',
    'lt2',
];

export const TipsMap = {
    [STSchemeColorVal.tx1]: i18n.t('文字1'),
    [STSchemeColorVal.bg1]: i18n.t('背景1'),
    [STSchemeColorVal.tx2]: i18n.t('文字2'),
    [STSchemeColorVal.bg2]: i18n.t('背景2'),
    [STSchemeColorVal.accent1]: i18n.t('着色1'),
    [STSchemeColorVal.accent2]: i18n.t('着色2'),
    [STSchemeColorVal.accent3]: i18n.t('着色3'),
    [STSchemeColorVal.accent4]: i18n.t('着色4'),
    [STSchemeColorVal.accent5]: i18n.t('着色5'),
    [STSchemeColorVal.accent6]: i18n.t('着色6'),
};

// 主题 model
export default class ThemeModel extends BaseModel implements IThemeModel {
    public static async convertDataSdkToUi(sdkData: any, uiData: any): Promise<void> {
        if (sdkData.props) {
            ThemePropertyConverter.convertPartialDataFromSdkToUi(sdkData.props, uiData);
        }
    }

    public static convertDataUiToSdk(uiData: any, sdkData: any): void {

    }

    declare public sdkData: IThemeDataModel | null;

    @observable name = '';
    @observable clrScheme: IClrScheme;
    // @observable tipsMap;

    public constructor() {
        super();
        this.type = UIModelType.THEME;
    }

    /**
     * 获取主题色盘颜色列表
     */
    public getThemeColorList = (isDark?: boolean, sliceIndex = 10): IThemeItem[] => Object.keys(this.clrScheme)
        .map(value => ({
            val: this.getMasterColorKeyFromTheme(Number(value), isDark),
            themeKey: this.getMasterColorKeyFromTheme(Number(value), isDark),
            color: this.clrScheme[value],
            tips: TipsMap[this.getMasterColorKeyFromTheme(Number(value), isDark)],
        }))
        .sort((a, b) => a.val - b.val)
        .slice(0, sliceIndex); // 默认值只要前10个;

    /**
     * 获取完整颜色列表
     */
    public getAllThemeColorList = (): IThemeItem[] => Object.keys(this.clrScheme)
        .map(value => ({
            val: Number(value),
            themeKey: Number(value),
            color: this.clrScheme[value],
            tips: TipsMap[value],
        }))
        .sort((a, b) => a.val - b.val);

    /**
     * 更新主题
     */
    public updateTheme = (updateThemeProps: IThemeSelectItem) => {
        const updateProps: ICTBaseStyles = {};
        const clrScheme: ICTColorScheme = {};
        const { title, colorSchema } = updateThemeProps;
        const keys = Object.keys(colorSchema);
        keys.forEach((key) => {
            const { color } = colorSchema[key];
            clrScheme[key] = {
                srgbClr: {
                    val: this.handleRgba2Hex(color),
                },
            };
        });

        updateProps.clrScheme = clrScheme;
        const themeSdkDataModel = this.sdkData as IThemeDataModel;
        themeSdkDataModel?.api?.updateTheme?.(updateProps, title);
    };

    /**
     * 主题映射母版key
     * 主题的色值key和母版的色值key存在一一对应关系
     * 在通过主题色盘设置元素填充等属性时，设置的是母版的key
     * 在直接修改主题时，则是通过主题key去修改
     */
    public getMasterColorKeyFromTheme = (key: STSchemeColorVal, isDark?: boolean): STSchemeColorVal => {
        switch (key) {
            case STSchemeColorVal.lt1:
                return isDark ? STSchemeColorVal.tx1 : STSchemeColorVal.bg1;
            case STSchemeColorVal.dk1:
                return isDark ? STSchemeColorVal.bg1 : STSchemeColorVal.tx1;
            case STSchemeColorVal.lt2:
                return isDark ? STSchemeColorVal.tx2 : STSchemeColorVal.bg2;
            case STSchemeColorVal.dk2:
                return isDark ? STSchemeColorVal.bg2 : STSchemeColorVal.tx2;
        }
        return key;
    };


    public updateThemeData = (themeData: DeltaOf<ICTBaseStyles>, name) => {
        const themeSdkDataModel = this.sdkData as IThemeDataModel;
        themeSdkDataModel?.api?.updateTheme?.(themeData, name);
    };

    private handleRgba2Hex = (color: string | null) => {
        if (!color) return DEFAULT_THEME_COLOR;

        if (color.startsWith('rgb')) {
            return rgba2hex(color).replace('#', '');
        }

        return color;
    };
    /**
     * 根据主题值获取主题的key
     */
    private getAllThemeKey = () => ({
        [STSchemeColorVal.lt1]: 'lt1',
        [STSchemeColorVal.lt2]: 'lt2',
        [STSchemeColorVal.dk1]: 'dk1',
        [STSchemeColorVal.dk2]: 'dk2',
        [STSchemeColorVal.accent1]: 'accent1',
        [STSchemeColorVal.accent2]: 'accent2',
        [STSchemeColorVal.accent3]: 'accent3',
        [STSchemeColorVal.accent4]: 'accent4',
        [STSchemeColorVal.accent5]: 'accent5',
        [STSchemeColorVal.accent6]: 'accent6',
        [STSchemeColorVal.hlink]: 'hlink',
        [STSchemeColorVal.folHlink]: 'folHlink',
    });

    /**
     * 获取tips，等设计定义
     */
    // private getTips = () => {};
}
